import {
  BackToTopButton,
  Footer as FooterComponent,
} from 'br2s-ui-componentlibrary_r2_02';
import AcraLogo from 'br2s-ui-componentlibrary_r2_02/dist/foundation/assets/ACRA_logo.svg';
import { SystemIcon } from 'br2s-ui-componentlibrary_r2_02/dist/foundation/assets/system-icon';
import { formatDate, isDateGreater } from '@/utilities/utilities';
import 'br2s-ui-componentlibrary_r2_02/dist/foundation/foundation.scss';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import './Footer.scss';

interface ISocial {
  label: string;
  icon: SystemIcon;
  href: string;
}

interface IMenus {
  label: string;
  href?: string;
  onCLick?: () => void;
}

interface IFooter {
  id: string;
  logo: string;
  socialLinks: ISocial[];
  menuLinks: IMenus[];
  copyright: string;
  updatedText?: string;
}

const Footer = ({
  containerRef,
}: {
  containerRef: React.RefObject<HTMLDivElement>;
}) => {
  const location = useLocation();
  const [mfeBuildDate, setMFEBuildDate] = useState('');
  const [bottomOffset, setBottomOffset] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const footerRef = useRef<HTMLDivElement>(null);

  const [buildDate] = (
    mfeBuildDate
      ? mfeBuildDate
      : (process.env.REACT_APP_MFE_DEPLOY_DATE as string)
  ).split(',');

  useEffect(() => {
    setMFEBuildDate('');
  }, [location.pathname]);

  const updateFooterDate = useCallback(
    (event: Event) => {
      const { detail } = event as CustomEvent<{
        buildDate: string;
        isMgnlDate?: boolean;
      }>;

      if (detail.isMgnlDate) {
        const newDate = formatDate(detail.buildDate.split(',')[0]);
        const oldDate = formatDate(buildDate.split(',')[0]);

        if (isDateGreater(newDate, oldDate)) {
          setMFEBuildDate(detail.buildDate);
        }
        return;
      }

      setMFEBuildDate(detail.buildDate);
    },
    [mfeBuildDate],
  );

  useEffect(() => {
    document.addEventListener('updateFooterDate', updateFooterDate);

    return () => {
      document.removeEventListener('updateFooterDate', updateFooterDate);
    };
  }, [updateFooterDate]);

  useEffect(() => {
    const script = document.createElement('script');
    script.setAttribute('type', 'text/javascript');
    script.setAttribute('src', 'https://webchat.vica.gov.sg/static/js/chat.js');
    script.setAttribute('referrerpolicy', 'origin');
    document.body.append(script);

    const handleScroll = () => {
      if (!footerRef.current) return;

      const footerRect = footerRef.current.getBoundingClientRect();
      const visibleHeight = Math.max(0, window.innerHeight - footerRect.top);
      setBottomOffset(visibleHeight + 16);

      const halfScreenHeight = window.innerHeight / 2;
      if (window.scrollY > halfScreenHeight) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    const observer = new ResizeObserver(handleScroll);

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => {
      script.remove();
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const footer: IFooter = {
    id: 'Footer-id',
    logo: AcraLogo,
    socialLinks: [
      {
        label: 'About Us',
        icon: SystemIcon.GeneralInfo,
        href: 'https://www.acra.gov.sg/who-we-are',
      },
      {
        label: 'News Room',
        icon: SystemIcon.DocumentDocument,
        href: 'https://www.acra.gov.sg/news-events',
      },
      {
        label: 'Guides',
        icon: SystemIcon.GeneralItemsCheck,
        href: 'https://www.acra.gov.sg/how-to-guides',
      },
      {
        label: 'Feedback',
        icon: SystemIcon.GeneralMessage,
        href: 'https://www.acra.gov.sg/feedback',
      },
      {
        label: 'Contact Us',
        icon: SystemIcon.GeneralPhone,
        href: 'https://www.acra.gov.sg/contact-us',
      },
    ],
    menuLinks: [
      {
        label: 'Report vulnerability',
        href: 'https://www.tech.gov.sg/report_vulnerability',
      },
      {
        label: 'Privacy statement',
        href: `${process.env.REACT_APP_SHELL_MFE_URL}/privacy-statement`,
      },
      {
        label: 'Terms of use',
        href: `${process.env.REACT_APP_SHELL_MFE_URL}/terms-of-use`,
      },
      {
        label: 'Sitemap',
        href: `${process.env.REACT_APP_SHELL_MFE_URL}/sitemap`,
      },
    ],
    copyright: `© ${new Date().getFullYear()} Government of Singapore`,
    updatedText: `Last updated on ${formatDate(buildDate)}`,
  };

  return (
    <div className="footer">
      <div
        id="webchat"
        app-id="acra-ada"
        app-name="Ask Ada"
        app-color="#f89828"
        app-icon={`${process.env.REACT_APP_SHELL_MFE_URL}/assets/ada.jpg`}
        app-subtitle="Powered by GovTech"
        app-base-font-size="15"
        app-welcome-message="Click here to chat with me."
        app-bot-response-trigger-event="true"
        app-launcher-animation-iteration="0"
        app-quick-launch-event="WelcomeMainMenu"
        app-position-right="40"
        app-position-bottom="40"
        app-enable-send-attachment="true"
        style={{
          bottom: bottomOffset + (isVisible ? 68 : 0),
        }}
      />
      {isVisible && (
        <BackToTopButton
          id="back-to-top"
          handleClick={scrollToTop}
          bottom={bottomOffset}
        />
      )}
      <div className="mfe-footer" ref={footerRef}>
        <FooterComponent
          id={footer.id}
          logo={footer.logo}
          copyright={footer.copyright}
          updatedText={footer.updatedText}
          menuLinks={footer.menuLinks}
          socialLinks={footer.socialLinks}
        ></FooterComponent>
      </div>
    </div>
  );
};

export default Footer;
